import React from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import cmsGovernanceLarge from '../assets/images/cms-governance-1536.webp';
import cmsGovernanceSmall from '../assets/images/cms-governance-768.webp';

const deliveredPatterns = [
  {
    title: 'Shared publishing, distinct sites',
    detail:
      'Earlier work used one CMS for separately branded sites, with site-specific navigation, content, and scoped editorial access. Staff could prepare content in English, Spanish, and French.',
  },
  {
    title: 'Structured content and review',
    detail:
      'Other delivered systems let administrators define fields and layouts, edit authenticated content, and move administrative briefings through department, division, and executive approval levels.',
  },
  {
    title: 'Content connected to operations',
    detail:
      'For The Garden Marquee, Scaled Tech built staff editing for venue pages, posts, galleries, and FAQs alongside inquiry and availability workflows.',
  },
];

const architecture = [
  {
    title: 'Who can edit?',
    detail:
      'Central rules could define content types, role boundaries, templates, and integration rules so people have access suited to their responsibilities.',
  },
  {
    title: 'What can vary between sites?',
    detail:
      'Site workspaces could give each site its own identity, navigation, local content, and language variants within the shared rules.',
  },
  {
    title: 'Who approves?',
    detail:
      'An editorial path could let an editor save a draft, request review, receive approval, and publish. The organization would agree on its stages and permissions.',
  },
  {
    title: 'Who owns upkeep?',
    detail:
      'Delivery and upkeep could include accessible page templates, agreed data connections, and assigned responsibilities for ongoing content review and maintenance.',
  },
];

function CmsGovernance() {
  return (
    <Container
      maxWidth='lg'
      sx={{ pt: { xs: 4, md: 7 }, '& .MuiTypography-root': { overflowWrap: 'anywhere' } }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
          alignItems: 'center',
          gap: { xs: 3, md: 5 },
          mb: 5,
        }}
      >
        <Box>
          <Typography
            component='h1'
            variant='h3'
            sx={{ fontWeight: 800, color: '#0B2545', fontSize: { xs: '2rem', md: '3rem' } }}
          >
            How a governed CMS can work
          </Typography>
          <Typography
            variant='body1'
            sx={{ mt: 2, color: '#475569' }}
          >
            Who can edit content? Who approves it? What can each site change, and who keeps it up to
            date? A governed CMS can help teams answer these questions while giving each site room
            to serve its audience. The delivered patterns below come from separate projects; the
            blueprint shows how they might be combined in a future project.
          </Typography>
        </Box>
        <Box
          component='figure'
          aria-label='CMS governance illustration'
          sx={{ m: 0, overflow: 'hidden', borderRadius: 3, bgcolor: '#F8FAFC' }}
        >
          <img
            src={cmsGovernanceLarge}
            srcSet={`${cmsGovernanceSmall} 768w, ${cmsGovernanceLarge} 1536w`}
            sizes='(max-width: 900px) calc(100vw - 48px), 540px'
            width={1536}
            height={1024}
            loading='eager'
            decoding='async'
            alt='Central content rules connect editorial review steps with distinct site workspaces.'
            style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '3 / 2' }}
          />
        </Box>
      </Box>

      <Box
        component='section'
        aria-labelledby='delivered-patterns'
      >
        <Typography
          id='delivered-patterns'
          component='h2'
          variant='h4'
          sx={{ mb: 3, fontWeight: 700 }}
        >
          Patterns from delivered work
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          {deliveredPatterns.map((pattern) => (
            <Box
              component='article'
              key={pattern.title}
              sx={{ p: 3, borderRadius: 3, bgcolor: '#F8FAFC' }}
            >
              <Typography
                component='h3'
                variant='h6'
                sx={{ mb: 1, fontWeight: 700 }}
              >
                {pattern.title}
              </Typography>
              <Typography color='text.secondary'>{pattern.detail}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        component='section'
        aria-labelledby='reference-architecture'
        sx={{ mt: 7 }}
      >
        <Typography
          variant='overline'
          sx={{ color: '#005596', fontWeight: 700 }}
        >
          Illustrative proposal, not one delivered platform
        </Typography>
        <Typography
          id='reference-architecture'
          component='h2'
          variant='h4'
          sx={{ fontWeight: 700 }}
        >
          Reference Architecture / Proprietary Platform Blueprint
        </Typography>
        <Typography
          variant='body1'
          sx={{ maxWidth: 780, mt: 2, mb: 3, color: '#475569' }}
        >
          This conceptual flow combines capabilities demonstrated in different projects. It is a
          starting point for discovery, not a claim that this complete platform has been deployed.
          Roles, stages, integrations, and accessibility checks would be designed for the buyer’s
          actual governance and content needs.
        </Typography>
        <Box
          component='figure'
          aria-labelledby='reference-architecture architecture-caption'
          sx={{ m: 0, p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: '#EAF4F8' }}
        >
          <Box
            component='ol'
            sx={{
              m: 0,
              pl: 3,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {architecture.map((step) => (
              <Box
                component='li'
                key={step.title}
                sx={{ pl: 1, pr: 2, overflowWrap: 'anywhere' }}
              >
                <Typography
                  component='h3'
                  variant='h6'
                  sx={{ fontWeight: 700 }}
                >
                  {step.title}
                </Typography>
                <Typography sx={{ color: '#405064' }}>{step.detail}</Typography>
              </Box>
            ))}
          </Box>
          <Typography
            id='architecture-caption'
            component='figcaption'
            variant='caption'
            sx={{ display: 'block', mt: 3, color: '#405064' }}
          >
            Text diagram: central rules guide distinct site workspaces; editors move content through
            review; approved content reaches templates and connected systems. All steps are
            illustrative and would be tailored to a real project.
          </Typography>
        </Box>
      </Box>

      <Box
        component='section'
        aria-labelledby='capability-statement'
        sx={{ mt: 6 }}
      >
        <Typography
          id='capability-statement'
          component='h2'
          variant='h4'
          sx={{ fontWeight: 700 }}
        >
          CMS capability statement
        </Typography>
        <Typography sx={{ mt: 2, color: '#475569' }}>
          Review a concise overview of our experience, services, and delivery approach.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
          <Button
            href='/cms-capabilities'
            data-track='capability_statement'
          >
            View capability statement
          </Button>
          <Button
            href='/cms-capabilities'
            data-track='capability_statement'
            download='scaled-tech-cms-capabilities.html'
          >
            Download capability statement (HTML)
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 6, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Button
          component={Link}
          to='/case-studies'
          endIcon={<ArrowForwardIcon />}
        >
          See delivered CMS work
        </Button>
        <Button
          component={Link}
          to='/contact-us'
        >
          Discuss your publishing workflow
        </Button>
      </Box>
    </Container>
  );
}

export default CmsGovernance;
